import AppIconButton from "../form/AppIconButton";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../form";
import { useMaterialUIController } from "../../context";
import { getReportChatShell } from "../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { adminDataAdaptorService } from "../../services/adminDataAdaptorService";

export type ChatAdaptorSelection = {
  id: string;
  name: string;
  type: string;
};

type Props = {
  selected: ChatAdaptorSelection[];
  onChange: (next: ChatAdaptorSelection[]) => void;
  disabled?: boolean;
  iconColor?: string;
  /** Controlled open state; when set, the parent owns opening/closing. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render only the panel (e.g. when opened from the composer tools palette). */
  hideTrigger?: boolean;
};

export default function ChatAdaptorPicker({ selected, onChange, disabled, iconColor, open: openProp, onOpenChange, hideTrigger }: Props) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const shell = getReportChatShell(theme);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const [adaptors, setAdaptors] = useState<ChatAdaptorSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const loadAdaptors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminDataAdaptorService.list();
      setAdaptors(
        (list || [])
          .filter((a) => a.enabled !== false && a.id)
          .map((a) => ({ id: a.id, name: a.name, type: a.type }))
      );
    } catch (e) {
      console.error("Failed to load data adaptors", e);
      setError(t("chat.adaptorPicker.loadFailed"));
      setAdaptors([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;
    void loadAdaptors();
    setFilter("");
  }, [open, loadAdaptors]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest(
          ".MuiAutocomplete-popper, .MuiPopover-root, .MuiModal-root, [role='listbox']"
        )
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, setOpen]);

  const selectedIds = new Set(selected.map((a) => a.id));

  const toggle = (adaptor: ChatAdaptorSelection) => {
    if (selectedIds.has(adaptor.id)) {
      onChange(selected.filter((a) => a.id !== adaptor.id));
      return;
    }
    onChange([...selected, adaptor]);
  };

  const filtered = adaptors.filter((a) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      a.name.toLowerCase().includes(q) ||
      a.type.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q)
    );
  });

  return (
    <Box ref={rootRef} sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {!hideTrigger && (
        <AppIconButton
          size="small"
          disabled={disabled}
          busyIgnore
          title={t("chat.tooltips.adaptors")}
          onClick={() => setOpen(!open)}
          sx={custom.components.chatIndexPicker.triggerButton(iconColor)}
          aria-label={t("chat.adaptorPicker.open")}
          color={selected.length > 0 || open ? "primary" : "default"}
        >
          <StorageRoundedIcon fontSize="small" />
        </AppIconButton>
      )}

      {open ? (
        <Paper
          elevation={8}
          sx={{
            ...custom.components.chatIndexPicker.popoverPaper(shell, controller.darkMode),
            position: "absolute",
            bottom: "100%",
            left: 0,
            mb: 1,
            zIndex: (theme) => theme.zIndex.modal,
            width: 360,
            maxWidth: "min(92vw, 360px)",
            maxHeight: "min(70vh, 480px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="subtitle2" sx={custom.components.chatIndexPicker.title(shell.assistantText)}>
            {t("chat.adaptorPicker.title")}
          </Typography>
          <Typography variant="caption" sx={custom.components.chatIndexPicker.hint(shell.muted)}>
            {t("chat.adaptorPicker.hint")}
          </Typography>

          {error ? (
            <Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
              {error}
            </Typography>
          ) : null}

          <AppTextField
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("chat.adaptorPicker.search")}
            sx={{ mb: 1 }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.5}
              sx={{
                ...custom.components.chatIndexPicker.chipsRow,
                overflow: "auto",
                flex: 1,
                maxHeight: 260,
              }}
            >
              {filtered.map((adaptor) => {
                const isSelected = selectedIds.has(adaptor.id);
                return (
                  <Chip
                    key={adaptor.id}
                    size="small"
                    label={`${adaptor.name} (${adaptor.type})`}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    clickable
                    onClick={() => toggle(adaptor)}
                  />
                );
              })}
              {filtered.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {adaptors.length === 0 ? t("chat.adaptorPicker.empty") : t("chat.adaptorPicker.noMatch")}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={custom.components.chatIndexPicker.actions}>
            <AppButton
              size="small"
              variant="outlined"
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
            >
              {t("chat.adaptorPicker.clear")}
            </AppButton>
            <AppButton size="small" variant="contained" onClick={() => setOpen(false)}>
              {t("common.save")}
            </AppButton>
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
